import debounce from "lodash/debounce"
import last from "lodash/last"
import head from "lodash/head"
import Checkbox from "../checkbox"
import Icon from "../icon"
import Dropdown from "../dropdown"
import DropdownItem from "../dropdown-item"
import Dragbar from "./dragbar"
import Filter from "./filter"
import Sorter from "./sorter"

const deepTraversal = (node) => {
  const nodes = []
  if (node != null) {
    const stack = []
    stack.push(node)
    while (stack.length !== 0) {
      const item = stack.pop()
      const { children } = item
      nodes.push(item)

      if (children && children.length) {
        children.slice().reverse().forEach(item => stack.push(item))
      }
    }
  }
  return nodes
}

export default {
  name: "mm-table-header",
  components: {
    "ui-checkbox": Checkbox,
    "ui-icon": Icon,
    "ui-dropdown": Dropdown,
    "ui-dropdown-item": DropdownItem,
    "ui-table-filter": Filter,
    "ui-table-sorter": Sorter,
    "ui-table-dragbar": Dragbar,
  },
  props: {
    columns: Array,
    fixed: String,
    store: Object,
    rowSelection: Object,
    showSelectColumn: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headerRows() {
      const columns = this.columns
      const rows = []
      this.setRows(rows, columns, 0)

      return rows
    },
    showSelectionTrigger() {
      return (
        this.rowSelection && this.rowSelection.selections && this.rowSelection.selections.length
      )
    },
    deepColumns() {
      return this.store.state.columns.reduce((result, columns) => result.concat(deepTraversal(columns)), [])
    },
    debounceResizeHandler() {
      return debounce(this.resizeHandler, 60)
    },
    heightList() {
      const { headerRowHeightList } = this.store.layout
      const minRowSpans = this.headerRows.map(row => Math.min.apply(null, row.map(col => col.rowSpan || 1)))
      let count = 0

      return minRowSpans.map(span => {
        const heights = headerRowHeightList.slice(count, count + span)
        count += span
        return heights.reduce((h, item) => h + item, 0)
      })
    },
  },
  watch: {
    "store.state.columns": {
      immediate: true,
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (this.$isServer || !this.$el || this.fixed) {
            return
          }
          const height = this.$el.offsetHeight
          const { headerHeight } = this.store.layout

          if (height !== headerHeight) {
            const trs = Array.from(this.$el.children).map(tr => tr.offsetHeight)
            this.store.setLayout("headerRowHeightList", trs)
            this.store.setLayout("headerHeight", height)
          }
        })
      },
    },
  },
  mounted() {
    if (!this.fixed) {
      window.addEventListener("resize", this.debounceResizeHandler)
    }
  },
  beforeDestroy() {
    if (!this.fixed) {
      window.removeEventListener("resize", this.debounceResizeHandler)
    }
  },
  methods: {
    resizeHandler() {
      this.store.setLayout("headerHeight", this.$el.offsetHeight)
    },
    getSiblingColumn(column, type) {
      const method = ({
        prev: head,
        next: last,
      })[type]
      const vector = ({
        prev: -1,
        next: 1,
      })[type]
      let current = column

      while (current.children && current.children.length > 0) {
        current = method(current.children)
      }

      let index = this.deepColumns.findIndex(item => item === current)
      let target = null

      if (~index) {
        index += vector
        while (index >= 0 && index <= (this.deepColumns.length - 1)) {
          if (!Array.isArray(this.deepColumns[index].children)) {
            target = this.deepColumns[index]
            break
          }
          index += vector
        }
      }

      return {
        target,
        current,
      }
    },
    setRows(rows, columns, rowIndex) {
      rows[rowIndex] = rows[rowIndex] || []

      columns.forEach(col => {
        rows[rowIndex].push(col)
        if (col.children && col.children.length) {
          this.setRows(rows, col.children, rowIndex + 1)
        }
      })
    },
    selectAllHandler(event, value) {
      this.store.selectAllHandler(value)
    },
    handleClick(e, value) {
      this.rowSelection.onSelectionChange(value, this.store.state.enabledRows)
    },
    renderDragbar(column, type) {
      const { current, target } = this.getSiblingColumn(column, type)
      const props = {
        props: {
          [`${type}Column`]: target,
        },
      }
      return target ? <ui-table-dragbar column={current} {...props} /> : ""
    },
  },
  render(h) {
    const { state, operations } = this.store

    return (
      <thead>
        {this.headerRows.map((row, rowIndex) => (
          <tr key={rowIndex} style={{ height: this.fixed ? `${this.heightList[rowIndex]}px` : "" }}>
            {this.showSelectColumn && rowIndex === 0 && (
              <th class="mm-table-selection-col" rowspan={row[0] && row[0].rowSpan}>
                <ui-checkbox
                  checked={state.selectAll}
                  onInput={val => (state.selectAll = val)}
                  indeterminate={state.indeterminate}
                  onChange={this.selectAllHandler}
                />
                {this.showSelectionTrigger && (
                  <ui-dropdown
                    onClick={this.handleClick}>
                    <ui-icon
                      name="chevrondown"
                      size="12px"
                      fill="#898989"
                    />
                    <template slot="dropdown">
                      {this.rowSelection.selections.map(item => <ui-dropdown-item name={item.value}>{item.label}</ui-dropdown-item>)}
                    </template>
                  </ui-dropdown>
                )}
              </th>
            )}
            {row.map((column, colIndex) => {
              return (<th
                key={colIndex}
                colSpan={column.colSpan}
                rowSpan={column.rowSpan}
                class={{ "mm-thead-th--active": operations.sortColumn === column }}
              >
                {this.renderDragbar(column, "prev")}
                <div
                  class="mm-table-cell"
                  style={{
                    textAlign: column.align || "left",
                  }}
                >
                  {typeof column.title === "function" ? column.title(h) : column.title}
                  {(column.sortable === true || (column.sortable !== false && (column.sorter || column.defaultSort))) && (
                    <ui-table-sorter column={column} store={this.store}/>
                  )}
                  {column.filters && column.filters.length && column.onFilter && (
                    <ui-table-filter column={column} store={this.store}/>
                  )}
                </div>
                {this.renderDragbar(column, "next")}
              </th>)
            })}
          </tr>
        ))}
      </thead>
    )
  },
}
